/**
 * /skill-tool · M04 Skill/Tool 管理（S3 Day11）
 */
import { createFileRoute, useRouterState } from "@tanstack/react-router";

import { M04Shell } from "@/components/m04/m04-shell";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";

export const Route = createFileRoute("/skill-tool")({
	component: SkillToolPage,
});

function SkillToolPage() {
	const hash = useRouterState({ select: (s) => s.location.hash });
	const view = getSkillToolView(hash);
	const copy = SKILL_TOOL_VIEW_COPY[view];

	return (
		<div id="skill-catalog" className="scroll-mt-6 space-y-4">
			<PageHeader title={copy.title} description={copy.description} help={copy.help} />
			<M04Shell view={view} />
		</div>
	);
}

type SkillToolView = "catalog" | "action-verify" | "write-audit";

function getSkillToolView(hash: string): SkillToolView {
	if (hash === "write-actions" || hash === "write-audit") return "write-audit";
	return hash === "skill-action-verify" || hash === "action-verify" ? "action-verify" : "catalog";
}

const skillToolHelpSections: PageHelp["sections"] = [
	{
		title: "这个页面用来做什么",
		items: [
			"查看每个 Skill 能力包是否启用，以及它绑定了哪个底层工具。",
			"Skill 是业务能力包，不是底层原子工具；工具只负责读取或写入动作。",
			"维护每个 Skill 的 prompt；主 Agent 总规则仍在提示词与话术维护。",
			"验证 Shopify 授权、商品读取、订单读取等外部依赖是否可用。",
			"用绑定工具沙盒测试底层 handler 返回，避免把权限问题误认为 Skill 判断问题。",
		],
	},
	{
		title: "关键字段说明",
		items: [
			"读取数据：只查询订单、商品、政策知识等事实，不会改动店铺数据。",
			"执行操作：可能取消订单、改地址、退款或发起退货；当前写操作默认保护性试跑，需要后续确认策略后才允许真实写入。",
			"风险等级：低风险通常可自动查询；高风险需要人工确认、审计和回滚预案。",
			"自动预取条件：系统在生成回复前会主动准备的事实，例如订单状态、物流状态或商品信息。",
		],
	},
	{
		title: "当前真实边界",
		items: [
			"Shopify shop/product 已接通，可以作为真实只读事实。",
			"Shopify 订单权限未获批，订单读取、客户数据、orderCreate 和 draftOrderCreate 会被 PCD 限制阻塞。",
			"沙盒通过不等于真实写入已执行；沙盒结果只证明参数、权限检查和保护性试跑路径可用。",
		],
	},
];

const SKILL_TOOL_VIEW_COPY: Record<
	SkillToolView,
	{ title: string; description: string; help: PageHelp }
> = {
	catalog: {
		title: "Skill 能力包",
		description: "查看 SK01-SK11 的业务能力、绑定工具、提示词归属和读写边界。",
		help: {
			title: "Skill 能力包说明",
			description: "这里用来确认客服 Agent 的业务能力包、底层工具、触发条件和维护边界。",
			visual: <SkillToolHelpVisual />,
			sections: skillToolHelpSections,
		},
	},
	"action-verify": {
		title: "Skill 调试",
		description: "先看 Skill 是否应执行和如何生成 tool_plan，再验证绑定工具沙盒结果。",
		help: {
			title: "Skill 调试说明",
			description:
				"这里把 Skill 判断测试和绑定工具沙盒拆开，避免把工具返回误读成 Skill 判断已正确。",
			visual: <SkillToolHelpVisual />,
			sections: skillToolHelpSections,
		},
	},
	"write-audit": {
		title: "写动作审计",
		description: "查看 dry-run 后进入确认队列的写动作、Q gate、幂等键和 live 执行结果。",
		help: {
			title: "写动作审计说明",
			description: "这里是 live write 前的运营确认入口，只处理已经形成审计记录的写动作。",
			visual: <SkillToolHelpVisual />,
			sections: [
				{
					title: "这里做什么",
					items: [
						"按状态查看 pending、live、rejected 写动作审计。",
						"查看 confirmation_plan、dry_run_action、q_gate、live_result 和 mutation_executed。",
						"在确认幂等键和安全门后执行 live write，或记录拒绝原因。",
					],
				},
				{
					title: "关键边界",
					items: [
						"主 Agent 不允许直接 live mutation，只能生成 plan-only dry-run。",
						"确认按钮只对 pending_confirmation 生效，执行后必须能回到审计记录复盘。",
					],
				},
			],
		},
	},
};

function SkillToolHelpVisual() {
	const steps = [
		["方案命中", "PB/branch 给出 allowed_skills"],
		["Skill prompt", "主 Agent 看到每个 Skill 的参数抽取要求"],
		["Skill 判断", "Skill runtime 生成 resolved_args 与 tool_plan"],
		["绑定工具", "只读 live 查询；写类先 dry-run"],
		["写动作审计", "确认、拒绝、Q gate 与 live_result"],
	] as const;
	return (
		<div
			className="rounded-md border border-surface-border bg-surface-subtle p-3"
			data-testid="m04-help-visual-flow"
		>
			<div className="grid gap-2 md:grid-cols-5">
				{steps.map(([title, detail], index) => (
					<div
						key={title}
						className="relative rounded-md border border-surface-border bg-surface-card p-3"
					>
						<div className="text-xs text-text-muted">0{index + 1}</div>
						<div className="mt-1 text-sm font-semibold text-text-primary">{title}</div>
						<div className="mt-1 text-xs leading-5 text-text-secondary">{detail}</div>
					</div>
				))}
			</div>
			<div className="mt-3 grid gap-2 text-xs leading-5 text-text-secondary md:grid-cols-3">
				<div className="rounded-md bg-surface-background px-3 py-2">
					Skill 是业务能力包；tool_id 只是它最终绑定的底层处理器。
				</div>
				<div className="rounded-md bg-surface-background px-3 py-2">
					Prompt 只影响参数挖掘和调用说明，不能绕过白名单、权限和写入安全门。
				</div>
				<div className="rounded-md bg-surface-background px-3 py-2">
					调试页先看 Skill 判断，再看绑定工具沙盒，两者分开复盘。
				</div>
			</div>
		</div>
	);
}
